/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect } from 'react'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import parsers from '@sportninja/common/reducers/parsers'
import { ROUTES } from '@sportninja/common/constants/app'

import { orgForm } from '../../components/Form/form-configs'
import { Flex } from '../../components/Layout'
import Picture from '../../components/Picture'
import useGetListItems from '../../components/List/useGetListItems'
import OptionButtonEdit from '../../components/List/OptionButtons/Edit'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import OptionButtonFavourite from '../../components/List/OptionButtons/Favourite'
import List from '../../components/List'
import EntityFormDelete from '../../components/Form/EntityFormDelete'
import EditEntitySubTitle from '../EditEntitySubTitle'
import MoreOptions from '../../components/List/OptionButtons/More'

const OrgCell = ({ item }) => (
  <Flex alignItems='center'>
    <Picture
      size='kxsmall'
      square
      fit
      imageId={getImageThumbnailId(item)}
      iconName='users'
      css={css`
        margin-right: 8px;
      `}
    />
    {item.name_full}
  </Flex>
)

const OrgList = ({ refreshKey, setCount = () => {}, slug }) => {
  const listProps = useGetListItems(slug, {
    onComplete: (response) => setCount(response.meta.pagination.count),
  })

  const {
    sort,
    direction,
    getItems,
    permissions,
    debouncedSearchTerm,
    features,
  } = listProps

  useEffect(() => {
    getItems()
  }, [refreshKey, sort, direction, debouncedSearchTerm])

  const headers = [
    {
      text: t('common:name'),
      sizePercent: 37,
      sort: 'name_full',
      Cell: OrgCell,
      to: (item) => `${ROUTES.ORG_ROOT}/${item.id}`,
    },
    {
      text: t('Web:subOrg', { count: 2 }),
      sizePercent: 12,
      minWidth: 90,
      sort: 'children_count',
      accessor: 'children_count',
    },
    {
      text: t('common:competition', { count: 2 }),
      sizePercent: 14,
      minWidth: 120,
      sort: 'schedules_count',
      accessor: 'schedules_count',
    },
    {
      text: t('common:team', { count: 2 }),
      sizePercent: 10,
      sort: 'teams_count',
      accessor: 'teams_count',
    },
  ]

  const options = (item) => {
    const permission = permissions[item.id]
    const feature = features?.[item.id]

    const onEdit = async (...args) => {
      const [values] = args
      const { imageData, ...form } = values

      if (form?.delete_image) {
        await req(`/organizations/${item.id}/image`, {
          method: 'DELETE',
        })
      }

      const body = JSON.stringify(form)
      const response = await req(`/organizations/${item.id}`, {
        method: 'PUT',
        body,
      })
      if (imageData) {
        await req(`/organizations/${item.id}/image`, {
          method: 'POST',
          body: imageData,
        })
      }
      await getItems()
      return response
    }

    const opts = [
      permission.update && (
        <OptionButtonEdit
          key='edit'
          form={orgForm(
            parsers[ENTITY_TYPES.org](item),
            false,
            false,
            feature?.privateHiddenLeagues
          )}
          onSubmit={onEdit}
          title={t('Web:editOrganization')}
          subTitle={
            <EditEntitySubTitle id={item.id} entityType={ENTITY_TYPES.org} />
          }
          Delete={
            permission.delete && (
              <EntityFormDelete
                entityType={ENTITY_TYPES.org}
                itemId={item.id}
                title={`Delete ${item.name_full}`}
                type={t('common:organization')}
                onComplete={getItems}
              />
            )
          }
        />
      ),
      <OptionButtonFavourite
        key='fav'
        entityType={ENTITY_TYPES.org}
        item={item}
        isFavourite={item.is_favorite}
      />,
      <MoreOptions
        key='more'
        options={[
          permission.delete && {
            key: 'delete',
            Component: OptionButtonDelete,
            componentProps: {
              useForceDelete: true,
              unmountOnClose: true,
              entityType: ENTITY_TYPES.org,
              itemId: item.id,
              title: `Delete ${item.name_full}`,
              type: t('common:organization'),
              iconName: 'trash',
              onComplete: getItems,
            },
          },
        ]}
      />,
    ]

    return opts.filter((o) => o !== undefined)
  }

  return (
    <List
      headers={headers.filter((o) => o !== undefined)}
      options={options}
      listProps={listProps}
      noItemsText={t('common:organization', { count: 2 })}
      showSearch
    />
  )
}

export default OrgList
